import React, { useEffect, useState } from 'react';
import { EntityPathOption } from '../../types';
import Button from '../ui/Button';
import ActionRow from '../ui/ActionRow';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../pilot-ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../pilot-ui/select';

interface Props {
  open: boolean;
  sourcePath: string;
  deviceCount: number;
  options: EntityPathOption[];
  submitting?: boolean;
  error?: string | null;
  onCancel: () => void;
  // Bulk-move + delete in one operator action. Returning a promise lets the
  // dialog show "Working…" until the chain finishes. FS-910: the move is of
  // devices on exactly sourcePath; the caller reports the moved count apart
  // from the delete, which can still be refused.
  onConfirm: (target: string) => Promise<void>;
}

const ReassignDevicesDialog: React.FC<Props> = ({
  open,
  sourcePath,
  deviceCount,
  options,
  submitting = false,
  error = null,
  onCancel,
  onConfirm,
}) => {
  const [target, setTarget] = useState('');

  // FS-910: every opening, and every source, starts with no target chosen.
  useEffect(() => {
    setTarget('');
  }, [open, sourcePath]);

  const choices = options.filter((o) => o.path !== sourcePath);
  const items = choices.map((option) => ({
    value: option.path,
    label: option.label ? `${option.path} — ${option.label}` : option.path,
  }));
  // A target that is no longer offered (the list refreshed) is no target.
  const ready = choices.some((o) => o.path === target);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !submitting) onCancel(); }}>
      <DialogContent showCloseButton={false} className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Reassign Devices</DialogTitle>
          <DialogDescription>
            <strong className="font-mono">{sourcePath}</strong> is the path of{' '}
            <strong>{deviceCount}</strong> device{deviceCount === 1 ? '' : 's'}. Pick
            a new entity path to move them to, then this option will be deleted.
          </DialogDescription>
          <p className="text-gray-700 text-sm mb-3">
            This moves exact matches only. Devices on a path under it stay where they
            are, and the delete is refused if any of them would be left without an
            option above them.
          </p>
          <label htmlFor="reassign-target" className="block text-sm font-medium text-gray-700 mb-1">
            Move devices to:
          </label>
          <Select items={items} value={target || null} onValueChange={(value) => setTarget(value ?? '')} disabled={submitting}>
            <SelectTrigger id="reassign-target" aria-describedby="reassign-help" title="Choose a path for devices currently on the path being deleted." className="w-full font-mono">
              <SelectValue placeholder="— select target path —" />
            </SelectTrigger>
            <SelectContent>
            {choices.map((opt) => (
              <SelectItem key={opt.path} value={opt.path}>
                {opt.label ? `${opt.path} — ${opt.label}` : opt.path}
              </SelectItem>
            ))}
            </SelectContent>
          </Select>
          <p id="reassign-help" className="text-xs text-muted-foreground">Only devices exactly on the current path move. Choose a target before continuing.</p>
          {error && (
            <div
              className="border border-status-error-border bg-status-error text-status-error-foreground p-3 rounded-md text-sm font-medium"
              role="alert"
            >
              {error}
            </div>
          )}
        </DialogHeader>
        <DialogFooter className="mx-0 mb-0 flex-wrap">
          <ActionRow
            align="end"
            className="flex-1"
            reason={!ready && 'Choose a target path to continue.'}
            reasonId="reassign-disabled-reason"
            reasonRole="status"
          >
            <Button
              variant="secondary"
              onClick={onCancel}
              disabled={submitting}
              title="Close this dialog; no devices move and the entity path is kept"
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => ready && onConfirm(target)}
              disabled={submitting || !ready}
              aria-describedby={!ready ? 'reassign-disabled-reason' : undefined}
              className="whitespace-nowrap"
              title={ready
                ? 'Move every device whose path is exactly this one to the path chosen above, then delete this option'
                : 'Choose the entity path to move the devices to first'}
            >
              {submitting ? 'Working…' : `Move ${deviceCount} & Delete`}
            </Button>
          </ActionRow>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReassignDevicesDialog;
